import { useState, useEffect, useCallback, FormEvent } from 'react'
import { superadminApi } from '@/services/api/superadmin'
import type { SuperadminHolderInfo } from '@/services/api/superadmin'
import { extractErrorMessage } from '@/utils/errorHandler'

type ManageableRole = 'superadmin' | 'director'

const ROLE_LABELS: Record<ManageableRole, string> = {
  superadmin: 'Superadmin',
  director: 'Direktor',
}

const ROLE_BADGE: Record<string, string> = {
  superadmin: 'bg-purple-100 text-purple-800',
  director: 'bg-indigo-100 text-indigo-800',
  admin: 'bg-blue-100 text-blue-800',
  teacher: 'bg-green-100 text-green-800',
  hr: 'bg-yellow-100 text-yellow-800',
}

/**
 * Superadmin-/Direktor-Verwaltung für die Direktor-Perspektive.
 *
 * Erstellen, befördern und Rollen entfernen für Superadmin und Direktor.
 * Admin-/Lehrkräfte-Verwaltung läuft über die Admin-Perspektive (H1).
 */
export function DirectorSuperadminPage() {
  const [holders, setHolders] = useState<SuperadminHolderInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const [showCreate, setShowCreate] = useState(false)
  const [showPromote, setShowPromote] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [createForm, setCreateForm] = useState({
    email: '', first_name: '', last_name: '', phone: '', notes: '', role: 'superadmin' as ManageableRole,
  })
  const [promoteForm, setPromoteForm] = useState({
    email: '', first_name: '', last_name: '', notes: '', role: 'superadmin' as ManageableRole,
  })

  const fetchHolders = useCallback(async () => {
    setLoading(true)
    try {
      const res = await superadminApi.listSuperadminHolders()
      setHolders(res)
      setError(null)
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Benutzer konnten nicht geladen werden',
        permissionError: 'Keine Berechtigung.',
      })
      console.error('Failed to load leadership holders:', err)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchHolders() }, [fetchHolders])

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await superadminApi.assignRole({
        email: createForm.email,
        first_name: createForm.first_name,
        last_name: createForm.last_name,
        phone: createForm.phone || undefined,
        notes: createForm.notes || undefined,
        role: createForm.role,
      })
      setShowCreate(false)
      setCreateForm({ email: '', first_name: '', last_name: '', phone: '', notes: '', role: 'superadmin' })
      setMessage(res.message || `${ROLE_LABELS[createForm.role]} erstellt`)
      await fetchHolders()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Erstellen' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handlePromote = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      let res
      if (promoteForm.role === 'superadmin') {
        res = await superadminApi.promoteToSuperadmin({
          email: promoteForm.email,
          first_name: promoteForm.first_name || undefined,
          last_name: promoteForm.last_name || undefined,
          notes: promoteForm.notes || undefined,
        })
      } else {
        res = await superadminApi.assignRole({
          email: promoteForm.email,
          first_name: promoteForm.first_name || undefined,
          last_name: promoteForm.last_name || undefined,
          notes: promoteForm.notes || undefined,
          role: 'director',
        })
      }
      setShowPromote(false)
      setPromoteForm({ email: '', first_name: '', last_name: '', notes: '', role: 'superadmin' })
      setMessage(res.message || `Benutzer zu ${ROLE_LABELS[promoteForm.role]} befördert`)
      await fetchHolders()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Befördern' }))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemoveRole = async (h: SuperadminHolderInfo, role: ManageableRole) => {
    const label = ROLE_LABELS[role]
    if (!confirm(`${label}-Rolle von "${h.email}" entfernen?`)) return
    setBusyId(`${h.id}:${role}`)
    setError(null)
    try {
      await superadminApi.removeSpecificRole(h.id, role, `Entfernt durch Direktor`)
      setMessage(`${label}-Rolle von ${h.email} entfernt`)
      await fetchHolders()
    } catch (err: unknown) {
      setError(extractErrorMessage(err, { loadError: 'Fehler beim Entfernen' }))
    } finally {
      setBusyId(null)
    }
  }

  const manageableRolesOf = (h: SuperadminHolderInfo): ManageableRole[] =>
    (['superadmin', 'director'] as ManageableRole[]).filter((r) => h.roles.includes(r))

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Superadmin- / Direktor-Verwaltung</h1>
          <p className="text-sm text-gray-500 mt-1">
            Superadmins und Direktoren erstellen, befördern und demotieren (vollständig auditiert).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowPromote(true)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            Befördern
          </button>
          <button onClick={() => setShowCreate(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
            + Erstellen
          </button>
        </div>
      </div>

      {message && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{message}</div>}
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rollen</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={6} className="px-6 py-4 text-center text-gray-500">Laden...</td></tr>
            ) : holders.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-4 text-center text-gray-500">Keine Superadmins oder Direktoren vorhanden</td></tr>
            ) : (
              holders.map((h) => (
                <tr key={h.id}>
                  <td className="px-6 py-4">{h.last_name}, {h.first_name}</td>
                  <td className="px-6 py-4">{h.email}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {h.roles.map((r) => (
                        <span key={r} className={`px-2 py-1 text-xs rounded-full ${ROLE_BADGE[r] || 'bg-gray-100 text-gray-600'}`}>
                          {ROLE_LABELS[r as ManageableRole] || r}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${h.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {h.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{new Date(h.created_at).toLocaleDateString('de-CH')}</td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex flex-wrap gap-2">
                      {manageableRolesOf(h).map((role) => (
                        <button
                          key={role}
                          onClick={() => handleRemoveRole(h, role)}
                          disabled={busyId === `${h.id}:${role}`}
                          className="text-orange-600 hover:text-orange-900 disabled:opacity-40"
                        >
                          {busyId === `${h.id}:${role}` ? '...' : `${ROLE_LABELS[role]}-Rolle entfernen`}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Benutzer erstellen</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Rolle *</label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as ManageableRole })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border"
                >
                  <option value="superadmin">Superadmin</option>
                  <option value="director">Direktor</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">E-Mail *</label>
                <input value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} type="email" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700">Vorname *</label>
                  <input value={createForm.first_name} onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Nachname *</label>
                  <input value={createForm.last_name} onChange={(e) => setCreateForm({ ...createForm, last_name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" /></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Telefon</label>
                <input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} type="tel" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notiz</label>
                <input value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <p className="text-xs text-gray-500">
                {createForm.role === 'superadmin'
                  ? 'Neue Superadmins erhalten automatisch zusätzlich die Admin-Rolle (Login-Rolle: Admin).'
                  : 'Neue Direktoren erhalten automatisch zusätzlich die Admin-Rolle (Dual-Role).'}
              </p>
              <div className="mt-6 flex justify-end space-x-3">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
                <button type="submit" disabled={submitting || !createForm.email || !createForm.first_name || !createForm.last_name} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Erstellen...' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPromote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Bestehenden Benutzer befördern</h2>
            <form onSubmit={handlePromote} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Ziel-Rolle *</label>
                <select
                  value={promoteForm.role}
                  onChange={(e) => setPromoteForm({ ...promoteForm, role: e.target.value as ManageableRole })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border"
                >
                  <option value="superadmin">Superadmin</option>
                  <option value="director">Direktor</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">E-Mail (bestehender Benutzer) *</label>
                <input value={promoteForm.email} onChange={(e) => setPromoteForm({ ...promoteForm, email: e.target.value })} type="email" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700">Vorname</label>
                  <input value={promoteForm.first_name} onChange={(e) => setPromoteForm({ ...promoteForm, first_name: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" /></div>
                <div><label className="block text-sm font-medium text-gray-700">Nachname</label>
                  <input value={promoteForm.last_name} onChange={(e) => setPromoteForm({ ...promoteForm, last_name: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" /></div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notiz</label>
                <input value={promoteForm.notes} onChange={(e) => setPromoteForm({ ...promoteForm, notes: e.target.value })} type="text" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button type="button" onClick={() => setShowPromote(false)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
                <button type="submit" disabled={submitting || !promoteForm.email} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Befördern...' : 'Befördern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
